import { useCallback, useEffect, useMemo, useState } from 'react';
import get from 'lodash/get';

/* Mutations & Queries */
import useSubscriptionsQueryEstimate from '@store/queries/subscription/useSubscriptionsQuery.estimate';
import useCartMutationCanAddService from '@store/queries/cart/useCartMutation.canaddservice';
import useCartMutationSelectplan from '@store/queries/cart/useCartMutation.selectplan';
import useCartMutationRemoveplan from '@store/queries/cart/useCartMutation.removeplan';

/* Utils */
import { mergeSkuData } from '@scenes/ServiceSkuPage/servicesku.utils';
import { skuPrices } from '@utils/sku';
import { getZipDataCookie } from '@utils/cookies/zipCodeTestCookie';
import { displayTimestamp } from '@utils/date';
import { formatPlansForReducer, getUpsellPlanIds } from '@utils/plans';
import { isMobileOrTablet } from '@utils/ui';

/* Constants */
import { shouldShowZipCodeTest } from '@components/Sku/ZipCodeEntry';
import { SERVICE_HEADER_TRIGGER_ID } from '@scenes/ServiceSkuPage/Parts/ServiceHeader/constants';
import { SPLITIONAME_CUSTOMER_ZIP } from '@integrations/split/split.constants';
import { BREAKDOWN_PRICE_TYPES, ITEM_REMOVAL_TOAST_MESSAGES } from '@constants/cart';

/* Hooks */
import useScrollTrigger from '@hooks/useScrollTrigger';
import useSplit from '@integrations/split/useSplit';

/* Client Store */
import useCartCache from '@store/queries/cart/useCart.cache';
import useUserCache from '@store/queries/user/useUser.cache';
import useServiceSkuStore, { ServiceSkuState } from '@scenes/ServiceSkuPage/store/store.servicesku';

/* Integrations */
import { SegmentTrackingAction } from '@integrations/segment/sku';
import plans from '@integrations/preval/plans.preval';

/* Types */
import { TPlansInfoFeatured } from 'types/plans.types';
import { Review } from 'types/reviews.types';
import { Sku } from 'types/sku.types';
import { PartnerBaseAttributes } from '@integrations/partners/partners.types';

/**
 * This encapsulates the logic for the ServiceInfo component, ie
 *  - upsell
 *  - zip code gate
 *  - scroll to reviews
 *
 * @param
 * @returns {{}}
 */
type TUseServiceInfo = {
  service: Sku;
  reviewsRef: React.RefObject<HTMLDivElement | null>;
  skuReviews?: Review[];
  partner?: PartnerBaseAttributes;
};

const useServiceInfo = ({ service, reviewsRef, skuReviews, partner }: TUseServiceInfo) => {
  const { isZipSuccessViewActive, setZipSuccessViewActive } = useServiceSkuStore((state: ServiceSkuState) => state.zipCodeGate);
  const [showPlanModal, setShowPlanModal] = useState<boolean>(false);
  const plansInfo = useMemo<TPlansInfoFeatured | null>(() => {
    if (!plans.length) return null;

    return formatPlansForReducer({
      planProducts: plans,
      featured: false,
      isMobileOrTablet: isMobileOrTablet(),
    });
  }, []);

  const [visible, setVisible] = useState(false);
  /*  Hooks */
  const cart = useCartCache();
  const user = useUserCache();

  useScrollTrigger({
    trigger: `#${SERVICE_HEADER_TRIGGER_ID}`,
    onReach: v => setVisible(v),
  });

  /* Split Hooks */
  const { treatment: geoZipEmailSplitTreatment, isSplitLoaded: geoZipEmailSplitLoaded } = useSplit(SPLITIONAME_CUSTOMER_ZIP);

  /* component constants */
  const reviews = skuReviews?.map(r => ({ ...r, date: displayTimestamp(r.timestamp) }));
  const subscriptionId = get(user, 'account.subscription.planId', null);
  const cartPlanId = get(cart, 'plan.id', null);
  const { monthlyID: MONTHLY_PLAN_ID } = getUpsellPlanIds({});

  /* QUERIES AND MUTATIONS ------------------------ */
  /* get subscriptions data, query */
  // const {data: {plans = []} = {}} = useSubscriptionsQueryAll({});
  const { data: { prices: subscriptionPrices = {} } = {} } = useSubscriptionsQueryEstimate({
    id: subscriptionId || cartPlanId || MONTHLY_PLAN_ID,
    skuId: service.id,
  });

  /* mutations */
  /* mutate: call later: book now */
  const { mutate: fetchCanAddService } = useCartMutationCanAddService({ skuId: service.id });
  /* end get subscriptions data */
  const selectPlanMutation = useCartMutationSelectplan();
  const removePlanMutation = useCartMutationRemoveplan();
  /* end mutations */

  /* END QUERIES AND MUTATIONS ------------------------ */

  /* create merged attributes into sku */
  const prices = skuPrices(service, cart!, subscriptionPrices);
  const isHourly = service?.priceType === BREAKDOWN_PRICE_TYPES.hourly;
  const showUpsell = (!user?.id || !user?.account?.hasSubscription) && !!prices.priceDifference && !isHourly;

  /* Determine Zip/Email Display */
  const { isBookingAllowed } = getZipDataCookie() || {};
  const showZipCodeTest =
    shouldShowZipCodeTest(isBookingAllowed, geoZipEmailSplitLoaded && geoZipEmailSplitTreatment, !!user?.id, !!partner?.id) &&
    geoZipEmailSplitTreatment;

  const extendedSkuAttributes: ReturnType<typeof mergeSkuData> = mergeSkuData({
    cart,
    user,
    showUpsell,
    showZipCodeTest,
    prices,
    sku: service,
  });

  const sku: typeof service & ReturnType<typeof mergeSkuData> & typeof plansInfo & typeof subscriptionPrices = useMemo(
    () => ({
      ...service,
      ...extendedSkuAttributes,
      plansInfo,
      subscriptionPrices,
      ...(service.content && { content: service.content }),
    }),
    [extendedSkuAttributes, plansInfo, service, subscriptionPrices],
  );

  const handleRating = (e: React.SyntheticEvent) => {
    e?.preventDefault?.();

    const element = document.getElementsByClassName('lazy-reviews')[0] as HTMLElement;

    if (element !== null && !reviewsRef.current) {
      window.scrollTo({
        top: element.offsetTop - sku.reviewsScrollOffset,
        behavior: 'smooth',
      });
    } else {
      window.scrollTo({
        top: (reviewsRef?.current?.offsetTop ?? 0) - sku.reviewsScrollOffset,
      });
    }
  };

  /* Toggle Modal & get Plan */
  const handleUpsellMore = useCallback(async () => {
    setShowPlanModal(true);
  }, []);

  /* Add/remove plan from upsell modal */
  const handleUpsellToggle = useCallback(
    (shouldAdd: boolean, planId = MONTHLY_PLAN_ID) => {
      if (shouldAdd) {
        selectPlanMutation.mutate({ planId });
        setShowPlanModal(false);
      } else {
        removePlanMutation.mutate({ toastMessage: ITEM_REMOVAL_TOAST_MESSAGES.DEFAULT });
      }
    },
    [MONTHLY_PLAN_ID, removePlanMutation, selectPlanMutation],
  );

  /* This is just nonsense. Now our buttons can be different actions in the various sub-headers. */
  const onHandleBookingClick = (e: React.SyntheticEvent) => {
    e?.preventDefault?.();

    if (showZipCodeTest) {
      window.scrollTo({
        top: 0,
      });

      return;
    }

    fetchCanAddService();
  };

  /* -------- USE EFFECTS --------------------- */
  /* fire segment on load & seed current sku view in cache */
  useEffect(() => {
    SegmentTrackingAction.skuPage(sku);
  }, [sku]);

  useEffect(() => {
    if (!isZipSuccessViewActive) setZipSuccessViewActive(true);

    return () => {
      if (isZipSuccessViewActive) setZipSuccessViewActive(false);
    };
  }, [isZipSuccessViewActive, setZipSuccessViewActive]);

  return {
    handleRating,
    /* Methods used outside of service info */
    handleUpsellMore,
    onHandleBookingClick,
    handleUpsellToggle,
    /* Plan concerns that sit outside of service info */
    showPlanModal,
    setShowPlanModal,
    /* updated sku object */
    sku,
    /* visible trigger to how sub headers on scroll */
    visible,
    plansInfo,
    reviews,
  };
};

export default useServiceInfo;
